import type { TripAccessorialDecisionInput } from '@chukua/common';
import { Input, InputNumber, Modal } from 'antd';
import { useState } from 'react';
import { accessorialLabel } from '../policies/accessorial-labels.policy';
import { decisionFormProblem } from '../policies/decision-input.policy';
import type { AccessorialProposal } from '../trip-accessorials.api';

interface DecisionModalProps {
  proposal: AccessorialProposal;
  mode: 'adjusted' | 'dropped';
  /** Resolves to the server's explanation when it refuses, or null once recorded. */
  onDecide: (input: TripAccessorialDecisionInput) => Promise<string | null>;
  onClose: () => void;
}

/** Adjusting changes the quantity only, the unit price stays the one the job was priced with; dropping needs only a reason. Both need the reason, kept with the decision. */
export function DecisionModal({ proposal, mode, onDecide, onClose }: DecisionModalProps) {
  const [quantity, setQuantity] = useState<number | null>(proposal.quantity);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const label = accessorialLabel(proposal.proposalKey);

  const submit = async () => {
    const problem = decisionFormProblem({ mode, quantity, reason });
    if (problem) return setError(problem);
    const base = { proposalKey: proposal.proposalKey, basisHash: proposal.basisHash, expectedDecisionId: proposal.decision?.id ?? null, reason: reason.trim() };
    setSubmitting(true);
    const refusal = await onDecide(mode === 'adjusted' ? { ...base, decision: 'adjusted', quantity: quantity ?? 1 } : { ...base, decision: 'dropped' });
    setSubmitting(false);
    if (refusal) return setError(refusal);
    onClose();
  };

  return (
    <Modal
      open
      title={`${mode === 'adjusted' ? 'Adjust' : 'Drop'} ${label.toLowerCase()}`}
      okText={mode === 'adjusted' ? 'Save adjustment' : 'Drop charge'}
      okButtonProps={{ danger: mode === 'dropped' }}
      confirmLoading={isSubmitting}
      onOk={() => void submit()}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4 pt-2">
        {mode === 'adjusted' && (
          <label className="flex flex-col gap-1 text-sm text-fg">
            Quantity (proposed {proposal.quantity})
            <InputNumber min={1} max={1000} precision={0} value={quantity} onChange={setQuantity} className="w-32" />
          </label>
        )}
        <label className="flex flex-col gap-1 text-sm text-fg">
          Reason
          <Input.TextArea rows={3} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        {error && (
          <p role="alert" className="text-sm text-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
